import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { TranslocoDirective } from '@jsverse/transloco';
import { MatButton } from '@angular/material/button';
import type { ConfigureParticipantDialogData } from './configure-participant-dialog-data';
import { MatError, MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { ValidatorErrorPipe } from '../../pipes/validator-error';
import { FIELD_VALIDATORS } from '../../../app.constants';
import { validateInteger } from '../../validators/integer.validator';
import { AssistDeviceService } from '../../../features/assist-device/assist-device.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { AssistDevicePatchDTO } from '../../../features/assist-device/model/assist-device-patch.dto';

@Component({
  selector: 'app-configure-participant-dialog',
  imports: [
    FormsModule,
    MatDialogTitle,
    TranslocoDirective,
    ReactiveFormsModule,
    MatDialogContent,
    MatButton,
    MatDialogActions,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    ValidatorErrorPipe,
  ],
  providers: [AssistDeviceService],
  templateUrl: './configure-participant-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfigureParticipantDialogComponent {
  private readonly assistDeviceService = inject(AssistDeviceService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject<MatDialogRef<ConfigureParticipantDialogComponent, boolean>>(MatDialogRef);
  private readonly data = inject<ConfigureParticipantDialogData>(MAT_DIALOG_DATA);

  protected readonly mindDeviceNameControl = new FormControl<string>({
    value: this.data.mindDeviceName,
    disabled: true,
  });

  protected readonly participantNameControl = new FormControl<string>({
    value: this.data.participantName,
    disabled: true,
  });

  protected readonly participantThresholdControl = new FormControl<number | null>(
    this.data.participantThreshold ?? null,
    {
      validators: [
        Validators.required,
        Validators.min(FIELD_VALIDATORS.PARTICIPANT_THRESHOLD_MIN),
        Validators.max(FIELD_VALIDATORS.PARTICIPANT_THRESHOLD_MAX),
        validateInteger(),
      ],
    },
  );

  protected readonly form = new FormGroup({
    participantThreshold: this.participantThresholdControl,
  });

  protected save(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid || this.participantThresholdControl.value === null) {
      return;
    }

    const patchData: AssistDevicePatchDTO = {
      participantThreshold: this.participantThresholdControl.value,
    };

    this.assistDeviceService
      .patch(this.data.mindDeviceId, patchData)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.dialogRef.close(true));
  }

  protected cancel(): void {
    this.dialogRef.close();
  }
}
